import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VendorlyLogo from "@/components/VendorlyLogo";
import { CheckCircle, XCircle, Spinner } from "@phosphor-icons/react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ status: "loading", message: "" });
  const { user, refresh } = useAuth();

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "Missing verification token." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`);
        setState({ status: "success", message: `Email confirmed: ${data.email}` });
        try { await refresh(); } catch {}
      } catch (e) {
        setState({ status: "error", message: e.response?.data?.detail || "Verification failed." });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col items-center justify-center p-6" data-testid="verify-email-page">
      <Link to="/" className="flex items-center gap-2 mb-8">
        <VendorlyLogo size={30} />
        <span className="font-heading font-bold text-xl tracking-tight">Vendorly</span>
      </Link>
      <div className="bg-white border border-brand-border rounded-3xl p-10 max-w-md w-full text-center">
        {state.status === "loading" && (
          <>
            <Spinner size={44} weight="duotone" className="text-brand-primary animate-spin mx-auto" />
            <h1 className="font-heading font-bold text-2xl mt-4">Confirming your email…</h1>
          </>
        )}
        {state.status === "success" && (
          <>
            <CheckCircle size={54} weight="duotone" className="text-brand-success mx-auto" data-testid="verify-success" />
            <h1 className="font-heading font-bold text-2xl mt-4">Email confirmed 🎉</h1>
            <p className="text-brand-text/70 mt-2 text-sm">{state.message}</p>
            <Link to={user ? "/app" : "/login"} className="mt-6 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-6 py-3 text-sm font-semibold" data-testid="verify-cta">
              {user ? "Go to dashboard" : "Sign in"}
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle size={54} weight="duotone" className="text-brand-error mx-auto" data-testid="verify-error" />
            <h1 className="font-heading font-bold text-2xl mt-4">Couldn't confirm</h1>
            <p className="text-brand-text/70 mt-2 text-sm">{state.message}</p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/app/settings" className="inline-flex items-center gap-2 border border-brand-border hover:bg-brand-bg rounded-full px-5 py-2.5 text-sm font-semibold">Resend from Settings</Link>
              <Link to="/login" className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full px-5 py-2.5 text-sm font-semibold">Sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
